/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import React, { forwardRef } from 'react';

import { css as customCss, jsx } from '@compiled/react';

import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';

export type AppStackSize = 'xxsmall' | 'xsmall' | 'small' | 'medium';

export type AppStackItemProps = {
	label?: string;
	size?: AppStackSize;
	testId?: string;
};

export type AppStackItem = {
	/** The logo component to render for this item */
	component: React.ComponentType<AppStackItemProps>;
	/** Accessible label for this logo */
	label?: string;
	/** A unique string that appears as data attribute `data-testid` in the rendered code, serving as a hook for automated tests. */
	testId?: string;
};

export interface AppStackProps {
	data: AppStackItem[];
	/** Accessible label for the list. Callers must supply a localised string. */
	label: string;
	/**
	 * Maximum number of items to display. Must be at least 1; values below 1
	 * are clamped to 1.
	 */
	maxCount?: number;
	/**
	 * Size of items in the app stack. Defaults to 'medium'
	 */
	size?: AppStackSize;
	/**
	 * A unique string that appears as data attribute `data-testid` in the rendered code, serving as a hook for automated tests.
	 * Attached to the app stack container and items; items testIDs use the format `<testid>--item-<index>` unless otherwise set via item props.
	 */
	testId?: string;
}

const ROTATION_PATTERN = [0, 6, 0, -8] as const;
const DEFAULT_MAX_ITEMS = 6;

const styles = cssMap({
	root: {
		alignItems: 'center',
		display: 'flex',
		listStyle: 'none',
		marginBlockEnd: token('space.0'),
		marginBlockStart: token('space.0'),
		marginInlineEnd: token('space.0'),
		marginInlineStart: token('space.0'),
		paddingBlockEnd: token('space.0'),
		paddingBlockStart: token('space.0'),
		paddingInlineEnd: token('space.0'),
		paddingInlineStart: token('space.0'),
	},
	item: {
		alignItems: 'center',
		display: 'flex',
		marginBlockEnd: token('space.0'),
		marginBlockStart: token('space.0'),
		position: 'relative',
	},
	// willChange: 'transform' promotes the element to its own compositor layer, forcing GPU
	// rasterisation and eliminating the jagged subpixel edges that appear on CSS rotate() without it.
	rotate6: { transform: 'rotate(6deg)', willChange: 'transform' },
	rotateMinus8: { transform: 'rotate(-8deg)', willChange: 'transform' },
});

const overlapStyles = cssMap({
	xsmall: { marginInlineStart: token('space.negative.050') },
	small: { marginInlineStart: token('space.negative.050') },
	medium: { marginInlineStart: token('space.negative.075') },
});
const overlapXxsmallStyle = customCss({ marginInlineStart: '-3px' });

// These padding properties prevent rotated elements from being clipped on the block and inline-end edges.
const clipPaddingStyle = customCss({ paddingBlockEnd: '1px', paddingBlockStart: '1px' });
const lastRotatedStyle = customCss({ paddingInlineEnd: '1px' });

const AppStack: React.ForwardRefExoticComponent<
	AppStackProps & React.RefAttributes<HTMLUListElement>
> = forwardRef<HTMLUListElement, AppStackProps>(
	(
		{ data, label, maxCount = DEFAULT_MAX_ITEMS, size = 'medium', testId },
		ref,
	): React.JSX.Element => {
		const visibleItems = data.slice(0, Math.max(1, maxCount));

		return (
			<ul ref={ref} aria-label={label} data-testid={testId} css={[styles.root, clipPaddingStyle]}>
				{visibleItems.map((item, index) => {
					const rotation = ROTATION_PATTERN[index % ROTATION_PATTERN.length];
					const isLast = index === visibleItems.length - 1;

					const Component = item.component;

					return (
						<li
							key={`${item.label ?? item.component.displayName ?? index}-${index}`}
							data-testid={item.testId ?? (testId ? `${testId}--item-${index}` : undefined)}
							css={[
								styles.item,
								index > 0 && size === 'xxsmall' && overlapXxsmallStyle,
								index > 0 &&
									size !== 'xxsmall' &&
									overlapStyles[size as 'xsmall' | 'small' | 'medium'],
								rotation === 6 && styles.rotate6,
								rotation === -8 && styles.rotateMinus8,
								isLast && rotation !== 0 && lastRotatedStyle,
							]}
						>
							<Component label={item.label} size={size} testId={item.testId} />
						</li>
					);
				})}
			</ul>
		);
	},
);

AppStack.displayName = 'AppStack';

export default AppStack;
